// SupplierDashboard.jsx
// A supplier logging into the admin panel used to land on the exact same
// "Admin Analytics" screen as an admin — platform-wide revenue, every user's
// counts, every order's status — none of it theirs to see (that data leak is
// fixed at the API level in dashboardReportsRoutes.js). This is what a
// supplier sees instead: just their own inbound quotations.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../config/axiosInstance";

const STATUS_META = {
  approved: { label: "Approved", tone: "success" },
  approved_by_sub_admin: { label: "In review", tone: "warning" },
  waiting_for_final_approval: { label: "In review", tone: "warning" },
  pending: { label: "Pending", tone: "info" },
  rejected: { label: "Rejected", tone: "danger" },
};

const bucket = (status) => {
  if (status === "approved") return "approved";
  if (status === "rejected") return "rejected";
  return "pending"; // pending / approved_by_sub_admin / waiting_for_final_approval / anything else
};

export default function SupplierDashboard() {
  const Navigate = useNavigate();
  const baseURL = process.env.REACT_APP_URL;
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    if (!user?.user_id) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const res = await axiosInstance.get(
          `${baseURL}/admin/quotation/receive/supplier/${user.user_id}`
        );
        if (!alive) return;
        const rows = res?.data?.data;
        setQuotations(Array.isArray(rows) ? rows : []);
        setErr("");
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.message || "Failed to load your dashboard");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [baseURL, user?.user_id]);

  const counts = useMemo(() => {
    const c = { total: quotations.length, approved: 0, pending: 0, rejected: 0 };
    quotations.forEach((q) => {
      c[bucket(q.status)] += 1;
    });
    return c;
  }, [quotations]);

  const recent = useMemo(() => quotations.slice(0, 6), [quotations]);

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="container">
          <div className="dashboard-header__main">
            <div className="dashboard-title-row">
              <h1 className="dashboard-title">
                Welcome{user?.name ? `, ${user.name}` : ""}
              </h1>
              <span className="live-dot">
                <span></span>Live
              </span>
            </div>
            <p className="dashboard-subtitle">
              Here's a summary of the quotations you've received from
              MyPackGenie.
            </p>
          </div>
        </div>
      </header>

      <main className="container">
        {loading ? (
          <div className="grid grid--four">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="skeleton skeleton__card" />
            ))}
          </div>
        ) : err ? (
          <div className="alert alert--danger">
            <p className="alert__title">Error loading dashboard</p>
            <p className="alert__text">{err}</p>
          </div>
        ) : (
          <>
            <div className="grid grid--four">
              <StatCard label="Total Quotations" value={counts.total} />
              <StatCard label="Pending / In Review" value={counts.pending} />
              <StatCard label="Approved" value={counts.approved} tone="success" />
              <StatCard label="Rejected" value={counts.rejected} tone="danger" />
            </div>

            <div className="grid mt-24" style={{ gridTemplateColumns: "1fr" }}>
              <div className="card card--stretch">
                <div className="card__header">
                  <div>
                    <h3 className="card__title">Recent Quotations</h3>
                    <p className="card__sub">Your latest inbound requests</p>
                  </div>
                  <a
                    className="card__link"
                    onClick={() => Navigate("/admin/supplier/inbound")}
                  >
                    View all →
                  </a>
                </div>
                <div className="card__body table-wrapper">
                  <table className="dash-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Quoted Price</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="empty">
                            No quotations yet.
                          </td>
                        </tr>
                      ) : (
                        recent.map((q) => {
                          const meta =
                            STATUS_META[q.status] || {
                              label: q.status || "—",
                              tone: "info",
                            };
                          return (
                            <tr key={q.order_assigned_id}>
                              <td>{q?.product?.title || "—"}</td>
                              <td className="bold">
                                {q.quotation_price
                                  ? `₹${Number(q.quotation_price).toLocaleString("en-IN")}`
                                  : "—"}
                              </td>
                              <td>
                                <span className={`pill pill--${meta.tone}`}>
                                  {meta.label}
                                </span>
                              </td>
                              <td className="ts">
                                {q.created_at
                                  ? new Date(q.created_at).toLocaleDateString("en-IN")
                                  : "—"}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <footer className="footer">
              Updated {new Date().toLocaleString("en-IN")} · MyPackGenie
              Supplier Console
            </footer>
          </>
        )}
      </main>

      <DashboardStyles />
    </div>
  );
}

function StatCard({ label, value, tone }) {
  return (
    <div className="card card--hover stat-card">
      <div className="stat-card__top">
        <p className="stat-card__title">{label}</p>
      </div>
      <div className="stat-card__row">
        <p
          className="stat-card__value"
          style={
            tone === "success"
              ? { color: "var(--color-success-text)" }
              : tone === "danger"
              ? { color: "var(--color-danger-text)" }
              : undefined
          }
        >
          {value}
        </p>
      </div>
    </div>
  );
}

// Same design tokens/classes as the admin dashboard (Dashboard/index.jsx),
// duplicated locally so this page has no dependency on the admin-only view.
function DashboardStyles() {
  return (
    <style>{`
.dashboard{ min-height: 100%; color: var(--color-text-primary); }
.dashboard *{ box-sizing:border-box; }
.dashboard .container{ max-width:100%; margin:0 auto; padding: 0; }
@keyframes dashPulseDot{ 0%,100%{ opacity:1; } 50%{ opacity:.35; } }
.dashboard-header{ margin-bottom: 22px; }
.dashboard-header .container{ display:flex; align-items:flex-end; justify-content:space-between; gap:16px; flex-wrap:wrap; }
.dashboard-title-row{ display:flex; align-items:center; gap:10px; }
.dashboard-title{ margin:0; font-size: 24px; font-weight: 800; letter-spacing:-.025em; color: var(--color-text-primary); }
.dashboard-subtitle{ margin:5px 0 0; font-size:13.5px; color: var(--color-text-secondary); }
.live-dot{ display:inline-flex; align-items:center; gap:6px; height:23px; padding:0 9px; border-radius:7px; background: var(--color-success-bg); color: var(--color-success-text); font-size:11px; font-weight:700; }
.live-dot span{ width:6px; height:6px; border-radius:50%; background: var(--color-success-text); animation: dashPulseDot 1.8s infinite; }
.card{ background: var(--color-card); border: 1px solid var(--color-border); border-radius: var(--radius-card); box-shadow: var(--shadow-card); overflow: hidden; }
.card--hover{ transition: box-shadow .2s ease, transform .12s ease; }
.card--hover:hover{ box-shadow: var(--shadow-card-hover); }
.card__header{ display:flex; align-items:center; justify-content:space-between; gap:12px; padding:16px 18px; border-bottom:1px solid var(--color-border-soft); }
.card__title{ margin:0; font-size: 14.5px; font-weight: 700; color: var(--color-text-primary); }
.card__sub{ margin:3px 0 0; font-size:12px; color: var(--color-text-secondary); }
.card__body{ padding: 18px; }
.card--stretch{ height: 100%; }
.card__link{ font-size:11px; font-weight:700; color: var(--color-primary); cursor:pointer; text-decoration:none; }
.card__link:hover{ text-decoration:underline; }
.stat-card{ padding: 18px; }
.stat-card__top{ display:flex; align-items:center; justify-content:space-between; }
.stat-card__title{ margin:0; font-size: 11.5px; font-weight:700; letter-spacing:.04em; text-transform: uppercase; color: var(--color-text-tertiary); }
.stat-card__row{ display:flex; align-items:baseline; gap:9px; margin-top:12px; }
.stat-card__value{ margin:0; font-size: 30px; line-height: 1; font-weight: 800; letter-spacing:-.03em; color: var(--color-text-primary); font-family: var(--font-mono); }
.grid{ display:grid; gap: 16px; }
.grid--four{ grid-template-columns: repeat(1, minmax(0,1fr)); }
@media (min-width: 768px){ .grid--four{ grid-template-columns: repeat(2, minmax(0,1fr)); } }
@media (min-width: 1200px){ .grid--four{ grid-template-columns: repeat(4, minmax(0,1fr)); } }
.table-wrapper{ overflow:auto; }
.dash-table{ width:100%; border-collapse: separate; border-spacing:0; min-width: 480px; }
.dash-table thead th{ text-align:left; font-size: 10.5px; font-weight:700; letter-spacing:.05em; text-transform: uppercase; color: var(--color-text-tertiary); background: var(--color-surface-2); padding: 9px 18px; border: none; border-bottom: 1px solid var(--color-border-soft); }
.dash-table tbody td{ padding: 13px 18px; border: none; border-top: 1px solid var(--color-border-soft); color: var(--color-text-primary); font-size:13px; vertical-align: middle; background: transparent; }
.dash-table tbody tr:hover td{ background: var(--color-hover); }
.dash-table .empty{ text-align:center; padding: 28px; color: var(--color-text-secondary); }
.bold{ font-weight: 700; color: var(--color-text-primary); font-family: var(--font-mono); }
.ts{ font-size:10.5px; color: var(--color-text-tertiary); font-family: var(--font-mono); }
.pill{ display: inline-flex; align-items:center; border-radius: var(--radius-pill); font-size: 11px; font-weight: 700; padding: 4px 10px; }
.pill--success{ background: var(--color-success-bg); color: var(--color-success-text); }
.pill--warning{ background: var(--color-warning-bg); color: var(--color-warning-text); }
.pill--danger{ background: var(--color-danger-bg); color: var(--color-danger-text); }
.pill--info{ background: var(--color-info-bg); color: var(--color-info-text); }
.footer{ text-align:center; font-size: 11.5px; color: var(--color-text-tertiary); padding: 26px 0 8px; }
.alert{ border-radius: var(--radius-md); padding: 14px 16px; border: 1px solid var(--color-danger-text); }
.alert--danger{ background: var(--color-danger-bg); border-color: var(--color-danger-text); color: var(--color-danger-text); }
.alert__title{ margin:0 0 6px; font-weight: 700; }
.alert__text{ margin:0; opacity:.9; }
.skeleton{ background: linear-gradient(90deg, var(--color-hover) 25%, var(--color-surface-2) 37%, var(--color-hover) 63%); background-size: 400% 100%; animation: shimmer 1.3s infinite; border-radius: var(--radius-card); }
.skeleton__card{ height: 150px; }
.mt-24{ margin-top: 16px; }
@keyframes shimmer{ 0%{ background-position: 100% 0; } 100%{ background-position: -100% 0; } }
    `}</style>
  );
}
